import { Link, NavLink } from 'react-router-dom';
import { motion, useReducedMotion } from 'framer-motion';
import { ArrowRightIcon, SparklesIcon, UserIcon } from 'lucide-react';
import { mainNav } from '../../data/navigation';
import { pageActions } from '../../data/pageActions';
import { salon } from '../../data/content';
import { shopLabels } from '../../data/shopLabels';
import { telHref } from '../../core/rules/contact';
import { ease } from '../../core/format/motion';
import { btnPrimaryBlock, btnSecondaryBlock } from '../../ui/styles';

/** Stagger per menu-item; 6 items = laatste start na 0,15 s. */
const ITEM_STEP_S = 0.03;
type Props = {
  id: string;
  accountLabel: string;
  onQuiz: () => void;
  onBooking: () => void;
};
const contactLink = 'inline-flex min-h-[44px] items-center text-[15px] text-ink underline decoration-ink/25 underline-offset-[5px]';

/**
 * Het mobiele menu: een eigen vlak dat het scherm onder de header vult (de pagina eronder staat
 * stil, dat regelt de header). Bovenaan de pagina's groot in de display-letter; daaronder de twee
 * vaste vervolgstappen (afspraak en huidtest), dan account en direct contact als tekst.
 * Sluiten (Escape, route, knop) regelt de header.
 *
 * Hoogte: 100dvh min de header (aankondiging 36px + balk 72px). `dvh` volgt de adresbalk van
 * mobiele browsers, dus de knoppen onderaan vallen nooit weg.
 */
export function MobileMenu({
  id,
  accountLabel,
  onQuiz,
  onBooking
}: Props) {
  const reduce = useReducedMotion();
  const item = (i: number) => reduce ? {} : {
    initial: {
      opacity: 0,
      y: 8
    },
    animate: {
      opacity: 1,
      y: 0
    },
    transition: {
      duration: 0.22,
      delay: 0.04 + i * ITEM_STEP_S,
      ease
    }
  };
  return <motion.div id={id} initial={{
    opacity: 0
  }} animate={{
    opacity: 1
  }} exit={{
    opacity: 0
  }} transition={{
    duration: 0.18,
    ease
  }} className="absolute inset-x-0 top-full h-[calc(100dvh-6.75rem)] overflow-y-auto overscroll-contain border-t border-line bg-shell lg:hidden" data-id="element-7893" data-mp="mykptc2mk78x">
      <nav aria-label="Mobiele navigatie" className="flex min-h-full flex-col px-6 pb-[max(1.5rem,env(safe-area-inset-bottom))] pt-4 sm:px-10" data-id="element-7894" data-mp="23fyo7k70jlyc">
        <ul data-id="element-7895" data-mp="3ven9cxglkssm">
          {mainNav.map((link, i) => <motion.li key={link.to} {...item(i)} className="border-b border-line" data-id="element-7896" data-mp="3vi19dz1sgpww" data-mpi={i}>
              {/* NavLink zet zelf aria-current="page"; hier ook zichtbaar met een klei-streepje. */}
              <NavLink to={link.to} className={({
            isActive
          }) => `flex min-h-[60px] items-center justify-between type-title leading-none ${isActive ? 'text-clay' : 'text-ink'}`} data-id="element-7897" data-mp="88w9cfza7fsp">
                {({
              isActive
            }) => <>
                    {link.label}
                    {isActive ? <span aria-hidden="true" className="h-px w-6 bg-clay" data-id="element-7898" data-mp="3lokessttv6s6" /> : <ArrowRightIcon className="h-4 w-4 text-ink/40" strokeWidth={1.5} aria-hidden="true" data-id="element-7899" data-mp="2xy7zuy0609s3" />}
                  </>}
              </NavLink>
            </motion.li>)}
        </ul>

        <motion.div {...item(mainNav.length)} className="mt-8 grid grid-cols-2 gap-2" data-id="element-7900" data-mp="1dafebqb89vny">
          <button type="button" onClick={onBooking} aria-haspopup="dialog" className={btnPrimaryBlock} data-id="element-7901" data-mp="zir27n0zb76z">
            {pageActions.book}
          </button>
          <button type="button" onClick={onQuiz} aria-haspopup="dialog" className={btnSecondaryBlock} data-id="element-7902" data-mp="1r95ddbhly5ey">
            <SparklesIcon className="h-4 w-4 text-clay" strokeWidth={1.5} aria-hidden="true" data-id="element-7903" data-mp="1w6dv5ayug7fo" />
            {pageActions.quiz}
          </button>
        </motion.div>

        <motion.div {...item(mainNav.length + 1)} className="mt-auto pt-10" data-id="element-7904" data-mp="1n6qpue2zuhhc">
          <Link to="/account" className="inline-flex min-h-[44px] items-center gap-2 text-[15px] text-ink" data-id="element-7905" data-mp="6tk3oo1iyhlq">
            <UserIcon className="h-4 w-4" strokeWidth={1.5} aria-hidden="true" data-id="element-7906" data-mp="bty17c5i295i" />
            {accountLabel}
          </Link>
          {/* Eén behandelaar: direct contact hoort bij de hand, als rustige tekst in plaats van tegels. */}
          <div className="mt-4 border-t border-line pt-4" data-id="element-7907" data-mp="maic3e0wmome">
            <p className="text-[13px] text-muted" data-id="element-7908" data-mp="hds0p401oh7b">
              {salon.street}, {salon.city}
            </p>
            <ul className="mt-1 flex flex-wrap gap-x-6" data-id="element-7909" data-mp="dcg76ne1vbtu">
              <li data-id="element-7910" data-mp="1a15pgetla6gc">
                <a href={salon.whatsapp} target="_blank" rel="noreferrer" className={contactLink} data-id="element-7911" data-mp="1a1nv22255x0x">
                  WhatsApp
                  <span className="sr-only" data-id="element-7912" data-mp="153m8v89f5hl8">{shopLabels.viaWhatsApp}</span>
                </a>
              </li>
              <li data-id="element-7913" data-mp="10ywghtdyyl52">
                <a href={telHref(salon.phone)} className={contactLink} data-id="element-7914" data-mp="112kaetnbt9kb">
                  Bellen
                </a>
              </li>
              <li data-id="element-7915" data-mp="3lrhufy29b1ns">
                <a href={salon.routeUrl} target="_blank" rel="noreferrer" className={contactLink} data-id="element-7916" data-mp="3lrq5dketjwwc">
                  Route
                  <span className="sr-only" data-id="element-7917" data-mp="3gqkc9lh8y7ma"> naar de salon {shopLabels.newTab}</span>
                </a>
              </li>
            </ul>
          </div>
        </motion.div>
      </nav>
    </motion.div>;
}